import React, { useState } from "react";
import { BadgeCheck, KeyRound, ShieldCheck, UserRound } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { authApi } from "../../../api/authApi";
import "./AdminProfile.css";

// Same password rules as the Register page
const PASSWORD_RULES = [
  { key: "length", label: "At least 8 characters", test: (p) => p.length >= 8 },
  {
    key: "uppercase",
    label: "At least 1 uppercase letter",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    key: "lowercase",
    label: "At least 1 lowercase letter",
    test: (p) => /[a-z]/.test(p),
  },
  { key: "digit", label: "At least 1 number", test: (p) => /\d/.test(p) },
  {
    key: "special",
    label: "At least 1 special character (!@#$%^&*)",
    test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p),
  },
];

const getStrength = (met) => {
  if (met === 0) return { text: "", level: "none" };
  if (met === PASSWORD_RULES.length) return { text: "Strong", level: "strong" };
  if (met >= 3) return { text: "Medium", level: "medium" };
  return { text: "Weak", level: "weak" };
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "A";

const AdminProfile = () => {
  const { user } = useAuth();
  const [passwords, setPasswords] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setPasswords((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (passwords.next !== passwords.confirm) {
      setError("The new passwords do not match.");
      return;
    }

    if (!PASSWORD_RULES.every((rule) => rule.test(passwords.next))) {
      setError("The new password does not meet the requirements below.");
      return;
    }

    setSaving(true);
    try {
      await authApi.changePassword(passwords.current, passwords.next);
      setMessage("Password updated successfully.");
      setPasswords({ current: "", next: "", confirm: "" });
    } catch (changeError) {
      setError(
        changeError.response?.data?.error ||
          changeError.message ||
          "Could not update password.",
      );
    } finally {
      setSaving(false);
    }
  };

  const rules = PASSWORD_RULES.map((rule) => ({
    label: rule.label,
    met: rule.test(passwords.next),
  }));
  const metCount = rules.filter((rule) => rule.met).length;
  const strength = getStrength(metCount);
  const showMatch = passwords.confirm.length > 0;
  const passwordsMatch = passwords.next === passwords.confirm;
  const inputType = showPasswords ? "text" : "password";
  const displayName = user?.FullName || "Administrator";

  return (
    <section className="admin-profile-page">
      <header className="ap-header">
        <div className="ap-heading-icon" aria-hidden="true">
          <UserRound size={22} />
        </div>
        <div className="ap-heading-copy">
          <span className="ap-eyebrow">ACCOUNT SETTINGS</span>
          <h2>My profile</h2>
          <p>Manage your account details and security preferences.</p>
        </div>
        <span className="ap-secure-badge">
          <ShieldCheck size={15} aria-hidden="true" />
          Secure account
        </span>
      </header>

      <div className="ap-grid">
        {/* Account information */}
        <div
          className="admin-card ap-card"
          role="region"
          aria-labelledby="ap-account"
        >
          <div className="ap-card-head">
            <span className="ap-card-icon ap-card-icon-profile" aria-hidden="true">
              <UserRound size={18} />
            </span>
            <div>
              <h3 id="ap-account">Account information</h3>
              <p>Your administrator identity and contact details.</p>
            </div>
          </div>

          <div className="ap-identity">
            <div className="ap-avatar" aria-hidden="true">
              {getInitials(user?.FullName)}
            </div>
            <div className="ap-identity-text">
              <strong>{displayName}</strong>
              <span className="ap-badge">
                <BadgeCheck size={13} aria-hidden="true" />
                Administrator
              </span>
            </div>
          </div>

          <dl className="ap-details">
            <div className="ap-detail">
              <dt>Name</dt>
              <dd>{displayName}</dd>
            </div>
            <div className="ap-detail">
              <dt>Email</dt>
              <dd>{user?.Email || "—"}</dd>
            </div>
            <div className="ap-detail">
              <dt>Role</dt>
              <dd>Administrator</dd>
            </div>
          </dl>
        </div>

        {/* Change password */}
        <div
          className="admin-card ap-card"
          role="region"
          aria-labelledby="ap-password"
        >
          <div className="ap-card-head">
            <span className="ap-card-icon ap-card-icon-security" aria-hidden="true">
              <KeyRound size={18} />
            </span>
            <div>
              <h3 id="ap-password">Password &amp; security</h3>
              <p>Use a strong, unique password to protect your account.</p>
            </div>
          </div>

          {message && (
            <div className="ap-alert is-success" role="status">
              {message}
            </div>
          )}
          {error && (
            <div className="ap-alert is-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="ap-field">
              <label htmlFor="ap-current">Current password</label>
              <input
                id="ap-current"
                type={inputType}
                autoComplete="current-password"
                value={passwords.current}
                onChange={handleChange("current")}
                required
              />
            </div>

            <div className="ap-field">
              <label htmlFor="ap-next">New password</label>
              <input
                id="ap-next"
                type={inputType}
                autoComplete="new-password"
                value={passwords.next}
                onChange={handleChange("next")}
                aria-describedby="ap-rules"
                required
              />
              {passwords.next.length > 0 && (
                <div className={`ap-strength is-${strength.level}`}>
                  <div className="ap-strength-bar">
                    <div
                      className="ap-strength-fill"
                      style={{
                        width: `${(metCount / PASSWORD_RULES.length) * 100}%`,
                      }}
                    />
                  </div>
                  <span className="ap-strength-text">{strength.text}</span>
                </div>
              )}
            </div>

            <div className="ap-field">
              <label htmlFor="ap-confirm">Confirm new password</label>
              <input
                id="ap-confirm"
                type={inputType}
                autoComplete="new-password"
                value={passwords.confirm}
                onChange={handleChange("confirm")}
                required
              />
              {showMatch && (
                <span
                  className={
                    passwordsMatch ? "ap-match is-success" : "ap-match is-error"
                  }
                  role="status"
                >
                  {passwordsMatch
                    ? "✓ Passwords match"
                    : "Passwords do not match"}
                </span>
              )}
            </div>

            <label className="ap-toggle">
              <input
                type="checkbox"
                checked={showPasswords}
                onChange={(event) => setShowPasswords(event.target.checked)}
              />
              <span>Show passwords</span>
            </label>

            <ul className="ap-rules" id="ap-rules" aria-label="Password rules">
              {rules.map((rule) => (
                <li
                  key={rule.label}
                  className={rule.met ? "ap-rule is-met" : "ap-rule"}
                >
                  <span className="ap-rule-icon" aria-hidden="true">
                    {rule.met ? "✓" : ""}
                  </span>
                  <span>{rule.label}</span>
                  <span className="ap-sr-only">
                    {rule.met ? " (met)" : " (not met yet)"}
                  </span>
                </li>
              ))}
            </ul>

            <div className="ap-actions">
              <button type="submit" className="ap-btn" disabled={saving}>
                {saving ? "Updating…" : "Update password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default AdminProfile;
